import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';

import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { CDNCachingConfig, PopularBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent, ScrollSpeed } from '@frontend/sports/common/core/feature/scroll-adapter';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { EventSubscriptionService } from '@frontend/sports/event-subscription/feature/event-subscription';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { BetBuilderBadgePosition, PopularBetsPodTypes } from '@frontend/sports/types/components/popular-bets';
import { WidgetContext, WidgetPage, WidgetType } from '@frontend/sports/types/components/widget';
import { PopularMultiBets, PopularMultiBetsPayload } from '@frontend/sports/types/components/widget/types';
import { DeviceService } from '@frontend/vanilla/core';
import { RxFor } from '@rx-angular/template/for';
import { cloneDeep, debounce, minBy } from 'lodash-es';
import { BehaviorSubject, Observable, Subject, asapScheduler, combineLatest, filter, forkJoin, map, of, shareReplay } from 'rxjs';

import { BetBuilderMarket } from '../../betbuilder/model/bet-builder.model';
import { BetBuilderMapperService } from '../../betbuilder/services/betbuilder-mapper.service';
import { EpcotConfigService } from '../../common/epcot-config.service';
import { WidgetContextService } from '../../widget/common/widget-context.service';
import { WidgetComponent } from '../../widget/core/widget-registry.service';
import { PopularBetsBetBuilderPlusPodComponent } from '../popular-bets-bet-builder-plus/popular-bets-bet-builder-plus-pod/popular-bets-bet-builder-plus-pod.component';
import { PopularBetsBetBuilderPodComponent } from '../popular-bets-bet-builder/popular-bets-bet-builder-pod/popular-bets-bet-builder-pod.component';
import { PopularBetsHammerGestureConfigProvider } from '../popular-bets-gesture-provider';
import { getMinPodHeight, getMinPodListWidth } from '../popular-bets-heights';
import { PopularBetsModuleHeaderComponent } from '../popular-bets-module-header/popular-bets-module-header.component';
import { PopularBetsPodComponent } from '../popular-bets-parlay/popular-bets-pod/popular-bets-pod.component';
import { PopularBetsPodBackgroundDirective } from '../popular-bets-pod-background/popular-bets-pod-background.directive';
import { PopularBetsBetBuilderDataService } from '../popular-bets-services/popular-bets-bet-builder-data.service';
import { PopularBetsExpandCollapseService } from '../popular-bets-services/popular-bets-expand-collapse.service';
import { IExpandCollapse, IPodHeightConfigModel, IPreCreatedOption, MultiBetsViewModel, PodEventsModel } from '../popular-bets.models';

@Component({
    imports: [
        AsyncPipe,
        RxFor,
        PopularBetsModuleHeaderComponent,
        PopularBetsPodComponent,
        ScrollAdapterComponent,
        PopularBetsBetBuilderPodComponent,
        PopularBetsPodBackgroundDirective,
        NgOptimizedImage,
        PopularBetsBetBuilderPlusPodComponent,
    ],
    selector: 'ms-popular-multi-bets-widget',
    templateUrl: './popular-multi-bets-widget.component.html',
    styleUrls: ['popular-multi-bets-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        PopularBetsExpandCollapseService,
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: PopularBetsHammerGestureConfigProvider,
        },
    ],
})
export class PopularMultiBetsWidgetComponent extends WidgetComponent<PopularMultiBetsPayload> implements OnDestroy, OnInit {
    @HostBinding('class') className = 'popular-multi-bets popular-bets-var-scope';
    @ViewChild(ScrollAdapterComponent) scrollComponent?: ScrollAdapterComponent;
    @ViewChild('badgeContainer', { read: ViewContainerRef }) container: ViewContainerRef;

    readonly isEpcotEnabled = this.epcotService.isEnabled();
    private minPodHeightConfig: IPodHeightConfigModel = {
        rows: 5,
        isEpcotEnabled: this.isEpcotEnabled,
        showOptionPick: false,
        podType: PopularBetsPodTypes.Parlay,
        isAllPodAreSameType: false,
        isAnyPodHasSummary: false,
    };

    readonly viewModel$ = new BehaviorSubject<MultiBetsViewModel>({
        title: '',
        titleIconClass: '',
        payload: [],
        minHeight: getMinPodHeight(this.minPodHeightConfig),
        minWidth: getMinPodListWidth(3),
        showHeader: false,
    });

    private eventSubscription?: EventSubscription<EventModel>;

    private readonly shouldIncludePayload = this.cdnCachingConfig.widgets.includes(WidgetType.PopularMultiBets);

    private config$ = this.popularBetsConfig.whenReady.pipe(
        map(() => this.popularBetsConfig),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    visiblePodLegs: number;
    defaultNoOfLegs = 0;
    isOptionPickEnabled = false;
    badgePosition = BetBuilderBadgePosition.OnModule;
    podType = PopularBetsPodTypes.Parlay;
    isAllPodAreSameType = false;
    isAnyPodHasSummary = false;
    isPodExpanded = false;
    itemsRendered = new Subject<PodEventsModel[]>();
    scrollSpeed: ScrollSpeed;

    constructor(
        private popularBetsConfig: PopularBetsConfig,
        private eventSubscriptionService: EventSubscriptionService,
        private changeDetectorRef: ChangeDetectorRef,
        private context: WidgetContextService,
        private cdnCachingConfig: CDNCachingConfig,
        private eventFactory: EventFactory,
        private fixtureFactory: FixtureFactory,
        private epcotService: EpcotConfigService,
        private popularBetsExpandCollapseService: PopularBetsExpandCollapseService,
        private deviceService: DeviceService,
        private betBuilderMapperService: BetBuilderMapperService,
        private popularBetsBetBuilderDataService: PopularBetsBetBuilderDataService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.expandCollapsePod();
        this.itemsRendered.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.scrollComponent?.setArrows();
            this.scrollSpeed = new ScrollSpeed({ default: 'slow' });
        });
    }

    protected override onData(data: PopularMultiBetsPayload): void {
        if (!data?.pods?.length) {
            this.getData()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((response) => this.renderData(response));
        } else {
            this.renderData(data);
        }
    }

    private renderData(data: PopularMultiBetsPayload): void {
        this.isOptionPickEnabled = data.pods.some((p) => p.enableSinglePickSelection);
        this.isAnyPodHasSummary = data.pods.some((p) => p.summary);
        this.badgePosition = data.betBuilderBadgePosition;

        this.viewModel$.next({
            ...this.viewModel$.value,
            titleIconClass: data.titleIconClass ?? '',
            showHeader: !!data.title || !!data.titleIconClass,
            minWidth: getMinPodListWidth(data.pods.length),
        });

        if (data?.pods?.some((pod) => pod.fixtures.length || pod.preCreatedOffer?.length)) {
            this.hasData = true;
            const firstPodType = data.pods[0].podType;
            this.isAllPodAreSameType = data.pods.every((p) => p.podType === firstPodType);
            this.podType = this.isAllPodAreSameType ? firstPodType : PopularBetsPodTypes.Parlay;

            if (this.podType === PopularBetsPodTypes.SGP && data.betBuilderBadgePosition === BetBuilderBadgePosition.OnModule) {
                this.loadBetBuilderBadgeComponent();
            }

            this.prepareViewModel(data)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((pods) => {
                    this.defaultNoOfLegs = pods[0].defaultNoOfLegs;
                    this.subscribe(pods);
                    this.minPodHeightConfig = {
                        ...this.minPodHeightConfig,
                        rows: pods[0].defaultNoOfLegs,
                        showOptionPick: this.isOptionPickEnabled,
                        podType: this.podType,
                        isAllPodAreSameType: this.isAllPodAreSameType,
                        isAnyPodHasSummary: this.isAnyPodHasSummary,
                    };

                    this.viewModel$.next({
                        ...this.viewModel$.value,
                        payload: pods,
                        title: this.getPodTitle(pods[0], data.title),
                        minHeight: getMinPodHeight(this.minPodHeightConfig),
                    });
                    this.setWidgetLoaded();
                    this.collapsePods();
                });
        } else {
            this.hasData = false;
            this.setWidgetLoaded();
            this.logEmptyData();
        }
    }

    override getContext(): Partial<WidgetContext> {
        const context = { ...super.getContext() };

        return !this.shouldIncludePayload ? context : { ...context, shouldIncludePayload: this.shouldIncludePayload };
    }

    trackByPod(_: number, pod: PodEventsModel): string | undefined {
        return pod.podId;
    }

    private getData(): Observable<PopularMultiBetsPayload> {
        return this.context.getWidget<PopularMultiBetsPayload>(this.getContext(), false).pipe(
            filter(({ payload }) => payload !== undefined),
            map(({ payload }) => payload as PopularMultiBetsPayload),
        );
    }

    private prepareViewModel(data: PopularMultiBetsPayload): Observable<PodEventsModel[]> {
        const biggestPod = Math.max(...data.pods.map((p) => p.fixtures.length));
        const podEventsObservables = data.pods.map((pod) => {
            if ((pod.podType === PopularBetsPodTypes.SGP || pod.podType === PopularBetsPodTypes.SGPPlus) && pod.preCreatedOffer) {
                return of(pod.preCreatedOffer.map((offer) => this.fixtureFactory.create(offer.sourceFixture, offer.sourceId)));
            }

            return this.eventFactory.create(pod.fixtures);
        });

        return combineLatest([forkJoin(podEventsObservables), this.config$]).pipe(
            map(([eventsArray, config]) => {
                return eventsArray.map((events, index) => {
                    return this.preparePodViewModelPayload(events, index, data.pods[index], config, biggestPod, data.visibleOptionsLimit);
                });
            }),
        );
    }

    private preparePodViewModelPayload(
        events: GridEvent[],
        position: number,
        pod: PopularMultiBets,
        config: PopularBetsConfig,
        biggestPod: number,
        visibleOptionsLimit: number,
    ): PodEventsModel {
        const preCreatedMarket = this.mapPreCreatedMarket(pod);
        const preCreatedOptions = this.getPreCreatedMultiProviderDetails(pod, events);

        const eventsCount =
            pod.podType === PopularBetsPodTypes.SGP || pod.podType === PopularBetsPodTypes.SGPPlus ? preCreatedOptions.length : events.length;
        const defaultLegs = Math.min(Math.max(eventsCount, biggestPod), visibleOptionsLimit);

        return {
            title: pod.title,
            events,
            counter: pod.counter > config.minCountForShowingCounter ? pod.counter : 0,
            sport: (pod.sportIds || pod.competitionIds) && this.isHomeOrMultiSportsLobby() ? events[0].sport : undefined,
            expand: false,
            visibleOptionsLimit: defaultLegs,
            showMoreLessButtonVisible: eventsCount > defaultLegs,
            showOptionPick: pod.enableSinglePickSelection,
            eventsCount,
            podId: pod.podId,
            tracking: this.getTrackingDetail(position, pod.podType, events.length),
            eventStartDate: this.getEarliestEventStartDate(events),
            defaultNoOfLegs: defaultLegs,
            isOptionPickEnabled: this.isOptionPickEnabled,
            podType: pod.podType,
            isAllPodAreSameType: this.isAllPodAreSameType,
            preCreatedMarket,
            preCreatedOptions,
            preCreatedOffer: pod.preCreatedOffer?.[0],
            summary: pod.summary,
            betBuilderBadgePosition: this.badgePosition,
            backgroundStyle: pod.backgroundStyle,
            enableLogoHeader: pod.enableLogoHeader,
            backgroundImage: pod.backgroundImage,
            podTheme: pod.podTheme,
            titleColor: pod.titleColor,
            podOptionTeamInfo: pod.popularBetsOptionTeamInfo,
        };
    }

    private getTrackingDetail(position: number, podType: PopularBetsPodTypes, noOfEvents: number): TrackingSource {
        const betDetailsBase =
            podType == PopularBetsPodTypes.SGP ? 'popular bet builder module - {{length}}' : 'popular bets - {{type}} - {{index}}p - {{length}}';

        return {
            source: '',
            betDetails: betDetailsBase,
            variant: noOfEvents.toString(),
            position,
        };
    }

    private subscribe(podPayload: PodEventsModel[]): void {
        this.unsubscribe();

        const callbacks = podPayload.flatMap((pod, podIndex) =>
            pod.events.map((event) => ({
                event,
                callback: debounce(this.getCallback(podIndex, event.id), 100),
            })),
        );

        if (callbacks.length) {
            this.eventSubscription = this.eventSubscriptionService.subscribe(callbacks);
        }
    }

    private getCallback(podIndex: number, eventId: string): (updatedEvent: EventModel) => void {
        return (updatedEvent) => {
            if (!updatedEvent) {
                return;
            }

            const currentPod = this.viewModel$.value.payload[podIndex];

            const eventIndex = currentPod.events.findIndex(({ id }) => id === eventId);
            if (eventIndex === -1) {
                return;
            }

            currentPod.events[eventIndex] = updatedEvent;
            this.viewModel$.value.payload[podIndex] = cloneDeep(currentPod);

            this.viewModel$.next({
                ...this.viewModel$.value,
            });
            this.changeDetectorRef.detectChanges();
        };
    }

    private isHomeOrMultiSportsLobby(): boolean {
        const contextPage = this.context.getContext().page;
        return contextPage === WidgetPage.HomeLobby || contextPage === WidgetPage.MultiSportsLobby;
    }

    private getEarliestEventStartDate(events: GridEvent[]): Date | undefined {
        return minBy(events, (e: GridEvent) => e.startDate)?.startDate;
    }

    private unsubscribe(): void {
        this.eventSubscription?.unsubscribe();
    }

    private getPodTitle(pod: PodEventsModel, title: string | undefined): string {
        return interpolateDoubleBracesString(title, { sport: this.isHomeOrMultiSportsLobby() ? '' : pod.events[0].sport.name });
    }

    private expandCollapsePod(): void {
        this.popularBetsExpandCollapseService
            .getExpandCollapse()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: IExpandCollapse) => {
                if (!data) return;

                this.visiblePodLegs = data.noOfLegs;
                this.isPodExpanded = data.isExpanded;
                this.minPodHeightConfig = {
                    ...this.minPodHeightConfig,
                    rows: this.visiblePodLegs,
                };
                this.viewModel$.next({
                    ...this.viewModel$.value,
                    minHeight: getMinPodHeight(this.minPodHeightConfig),
                });
                this.changeDetectorRef.detectChanges();
            });
    }

    collapsePods(): void {
        if (this.isPodExpanded) {
            this.popularBetsExpandCollapseService.setExpandCollapse(false, this.defaultNoOfLegs);
        }
    }

    async loadBetBuilderBadgeComponent(): Promise<void> {
        const { BetBuilderBadgeComponent } = await import('../../betbuilder/components/badge/bet-builder-badge/betbuilder-badge.component');
        this.container.clear();
        const badgeComponent = this.container.createComponent(BetBuilderBadgeComponent);
        badgeComponent.changeDetectorRef.detectChanges();
    }

    onPodScroll(): void {
        this.collapsePods();
    }

    onPodSwipe(): void {
        if (this.deviceService.isMobile) {
            // TODO: More improvements in swipe behavior is planned in future. Need to identify other ways to wait till completion of scroll-snap
            // And the story is planned for the same to reduce the collapse time
            asapScheduler.schedule(() => this.collapsePods(), 500);
        }
    }

    shouldShowBetBuilderPod(pod: PodEventsModel): boolean {
        return pod.podType === PopularBetsPodTypes.SGP && pod.eventsCount > 0;
    }

    shouldShowBetBuilderPlusPod(pod: PodEventsModel): boolean {
        return pod.podType === PopularBetsPodTypes.SGPPlus && pod.eventsCount > 0;
    }

    mapPreCreatedMarket(pod: PopularMultiBets): BetBuilderMarket | undefined {
        if (pod.podType === PopularBetsPodTypes.SGP && pod.preCreatedOffer?.length) {
            //Currently only one option can be configured for pod, change needed when we support multiple fixture and multiple options
            const { precreatedMarketsInfo } = pod.preCreatedOffer[0];
            if (precreatedMarketsInfo?.length) {
                const preCreated = this.betBuilderMapperService.mapFixture(pod.preCreatedOffer[0]);

                return (preCreated?.ambassadorsMarkets ?? []).concat(preCreated?.promotedMarkets ?? [], preCreated?.themedTabMarkets ?? [])[0];
            }
        }

        return undefined;
    }

    private getPreCreatedMultiProviderDetails(pod: PopularMultiBets, events: GridEvent[]): IPreCreatedOption[] {
        if ((pod.podType === PopularBetsPodTypes.SGP || pod.podType === PopularBetsPodTypes.SGPPlus) && pod.preCreatedOffer?.length) {
            return pod.preCreatedOffer.flatMap((offer) => {
                if (offer.precreatedGroupedInfo.length) {
                    return this.popularBetsBetBuilderDataService.getPreCreatedOptions(offer, events, pod.podType, pod.popularBetsOptionTeamInfo);
                }
                if (offer.precreatedMarketsInfo.length) {
                    return this.popularBetsBetBuilderDataService.getPreCreatedMarkets(offer, events, pod.podType);
                }
                return [];
            });
        }

        return [];
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }
}
